import React, { FC, useCallback, useEffect } from 'react';
import AppTheme, { IAppTheme } from '../../../resources/theme';
import injectSheet, { WithSheet } from 'react-jss';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { IAppState } from '../../../reducer/@types';
import { Product } from '../../../models/product';
import { PARTNERS_RECEIVED } from '../../../reducer/actions';
import ChipToggle from '../../ChipToggle';
import { applyFilter } from '../../../reducer';
import Button from '../../Buttons/Button';
import Divider from '../DetailComponents/Divider';
import { groupByFirstLetter } from '../../../resources/utils';
import AlphabetsButtonBar from '../../Buttons/AlphabetsButtonBar';
import clsx from 'clsx';
import SidebarChipItem from '../../SidebarChipItem';
import ContactButton from '../../ContactButton';
import Delay from '../../Delay';

// eslint-disable-next-line no-use-before-define
interface PartnersProps extends WithSheet<typeof STYLES> {

}

const selectPartners = createSelector<IAppState, IAppState, Pick<IAppState, 'partners' | 'filter' | 'selectedSectors'>>(
    state => state,
    ({ partners, filter, selectedSectors }) => ({ partners, filter, selectedSectors })
)

const Partners: FC<PartnersProps> = ({ classes }) => {

    const { partners = [], filter, selectedSectors = [] } = useSelector(selectPartners);

    const dispatch = useDispatch();

    const getData = useCallback(async (filter?: any) => {
        const data = await Product.fetchPartners(filter).catch(error => { throw error });
        dispatch({ type: PARTNERS_RECEIVED, data });
    }, [dispatch]);

    const handleClick = (id: string) => {
        let arr = filter.type === 'partner' ? filter.id as string[] || [] : [];
        let i = arr.indexOf(id);
        i === -1 ? arr.push(id) : arr.splice(i, 1);
        applyFilter('partner', arr);
    }

    const handleClear = () => applyFilter('', '');

    useEffect(() => {
        let filter = selectedSectors.map(item => ({ 'sector.airtableId': item }));
        getData({ where: { "$or": filter } });
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSectors]);

    let groupedData = groupByFirstLetter({ data: ((partners || [])) });

    const handleAlphabetClick = (alphabet: string) => {
        let el = window.document.getElementById(`partner-alphabet-group-${alphabet}`);
        if (el) {
            el.scrollIntoView({ block: 'start' });
        }
    }

    return (
        <div className={classes.root} >
            <div className={classes.chipsContainer} >
                {
                    (filter.id || []).length > 0 && filter.type === 'partner' ?
                        <div className={classes.clearButton} >
                            <Button onClick={handleClear} >{`[x] Clear`}</Button>
                            <Divider size='full' />
                        </div> : null
                }
                {
                    (groupedData || []).map((grpItems, i) => {
                        return (
                            <div key={i} className={clsx(classes.chipItem)} id={`partner-alphabet-group-${grpItems.group.toUpperCase()}`} >
                                <p className={classes.stickyLabel}>{grpItems.group}</p>
                                {
                                    grpItems.items.map((partner, i) => {
                                        return (
                                            <div key={partner.airtableId} className={clsx(classes.chipItem)} >
                                                <ChipToggle
                                                    title={<SidebarChipItem name={partner.name} total={partner.total} type="P" />}
                                                    active={(filter.id as string[] || []).indexOf(partner.airtableId) !== -1}
                                                    id={partner.airtableId}
                                                    onToggle={handleClick}
                                                    inactiveColor={'#ecf1fd'}
                                                    inactiveTextColor={AppTheme.color.primaryLight}
                                                    activeColor={'#fff4d5'}
                                                    maxWidth={150}
                                                    activeTextColor={AppTheme.color.primaryLight}
                                                />
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        )
                    })
                }
                <Delay duration={600}>
                    <ContactButton />
                </Delay>
            </div>
            <AlphabetsButtonBar groups={groupedData.map(item => item.group)} handleAlphabetClick={handleAlphabetClick} />
        </div>
    )
}

const STYLES = (theme: IAppTheme) => ({
    root: {
        display: 'flex',
        overflowX: 'hidden',
    },
    chipsContainer: {
        flex: 1,
        height: 'calc(100vh - 150px)',
        overflowY: 'auto'
    },
    groupItem: {
        '& > p': {
            marginLeft: 12
        },
        marginBottom: 8
    },
    chipItem: { marginBottom: 8 },
    clearButton: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        '& button': {
            // transition: '400ms ease-in-out',
            background: 'transparent',
            color: theme.color.primaryLight,
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            '&:focus': {
                outline: 'none',
            },
            '&:active': {
                // transform: 'rotate(360deg)'
            }
        }
    },
    stickyLabel: {
        position: 'sticky',
        top: 0,
        background: 'white',
        zIndex: 1,
    },
});

export default injectSheet(STYLES)(Partners);